import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAnuncios, useFavoritos, useChat } from '../hooks/useAnuncios';
import { useAuthStore } from '../store/authStore';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import { PageLoading, ErrorState } from '../components/common/Loading';
import { Textarea } from '../components/common/Input';

const AnuncioDetalhe: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, usuario } = useAuthStore();
  const { anuncioAtual, buscarAnuncio, isLoading } = useAnuncios();
  const { isFavorito, toggleFavorito } = useFavoritos();
  const { iniciarConversa } = useChat();

  const [selectedImage, setSelectedImage] = useState(0);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (id) {
      buscarAnuncio(id);
    }
  }, [id]);

  if (isLoading) {
    return <PageLoading message="Carregando anúncio..." />;
  }

  if (!anuncioAtual) {
    return (
      <div className="container-custom py-12">
        <ErrorState
          title="Anúncio não encontrado"
          message="O anúncio que você procura não existe ou foi removido."
          onRetry={() => navigate('/anuncios')}
        />
      </div>
    );
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const handleContact = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/anuncios/${id}` } });
      return;
    }

    if (!mensagem.trim()) return;

    setIsSending(true);
    try {
      const conversaId = await iniciarConversa(anuncioAtual.id, mensagem);
      if (conversaId) {
        navigate(`/chat/${conversaId}`);
      }
    } finally {
      setIsSending(false);
      setShowContactModal(false);
    }
  };

  const handleFavorite = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/anuncios/${id}` } });
      return;
    }
    toggleFavorito(anuncioAtual.id);
  };

  const isOwner = usuario?.id === anuncioAtual.vendedor.id;
  const isFav = isFavorito(anuncioAtual.id);
  const images = anuncioAtual.imagens || [];
  const mainImage = images[selectedImage]?.url || '/placeholder-animal.jpg';

  return (
    <div className="min-h-screen bg-gray-50 pb-24 lg:pb-8">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="container-custom py-4">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-green-700">Início</Link>
            <span className="text-gray-300">/</span>
            <Link to="/anuncios" className="text-gray-500 hover:text-green-700">Anúncios</Link>
            <span className="text-gray-300">/</span>
            <Link to={`/anuncios?categoria=${anuncioAtual.categoria}`} className="text-gray-500 hover:text-green-700 capitalize">
              {anuncioAtual.categoria}
            </Link>
            <span className="text-gray-300">/</span>
            <span className="text-gray-900 font-medium truncate max-w-[200px]">{anuncioAtual.titulo}</span>
          </nav>
        </div>
      </div>

      <div className="container-custom py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Images */}
          <div className="lg:col-span-2 space-y-4">
            {/* Main Image */}
            <div
              className="relative aspect-[4/3] bg-gray-100 rounded-2xl overflow-hidden cursor-pointer"
              onClick={() => setShowImageModal(true)}
            >
              <img
                src={mainImage}
                alt={anuncioAtual.titulo}
                className="w-full h-full object-cover"
              />
              {anuncioAtual.destaque && (
                <span className="absolute top-4 left-4 bg-amber-500 text-white text-sm font-bold px-3 py-1.5 rounded-full flex items-center gap-1">
                  ⭐ Destaque
                </span>
              )}
              <div className="absolute bottom-4 right-4 bg-black/60 text-white text-sm px-3 py-1.5 rounded-full">
                {selectedImage + 1} / {images.length || 1}
              </div>
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((img, index) => (
                  <button
                    key={img.id}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === index
                        ? 'border-green-600 ring-2 ring-green-200'
                        : 'border-transparent opacity-70 hover:opacity-100'
                    }`}
                  >
                    <img src={img.url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Description */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Descrição</h2>
              <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                {anuncioAtual.descricao}
              </p>
            </div>

            {/* Características */}
            {anuncioAtual.caracteristicas && anuncioAtual.caracteristicas.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Características</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {anuncioAtual.caracteristicas.map((car, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm text-gray-500">{car.nome}</p>
                      <p className="font-semibold text-gray-900">{car.valor}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Info & Actions */}
          <div className="space-y-4">
            {/* Price Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-24">
              <div className="mb-4">
                <span className="text-sm font-medium text-green-700 uppercase tracking-wide">
                  {anuncioAtual.categoria}
                  {anuncioAtual.subcategoria && ` • ${anuncioAtual.subcategoria}`}
                </span>
                <h1 className="text-2xl font-bold text-gray-900 mt-1">
                  {anuncioAtual.titulo}
                </h1>
              </div>

              <div className="flex items-center gap-2 text-gray-500 mb-4">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{anuncioAtual.localizacao?.cidade}, {anuncioAtual.localizacao?.estado}</span>
              </div>

              <div className="border-t border-gray-100 pt-4 mb-6">
                <p className="text-4xl font-bold text-green-700">
                  {formatPrice(anuncioAtual.preco)}
                </p>
                {anuncioAtual.precoNegociavel && (
                  <span className="text-sm text-gray-500">Preço negociável</span>
                )}
              </div>

              {!isOwner ? (
                <div className="space-y-3">
                  <Button
                    fullWidth
                    size="lg"
                    onClick={() => setShowContactModal(true)}
                    leftIcon={
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    }
                  >
                    Entrar em Contato
                  </Button>

                  {anuncioAtual.vendedor.whatsapp && (
                    <Button
                      as="link"
                      href={`https://wa.me/55${anuncioAtual.vendedor.whatsapp.replace(/\D/g, '')}`}
                      fullWidth
                      variant="secondary"
                      leftIcon={
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                      }
                    >
                      WhatsApp
                    </Button>
                  )}

                  <Button
                    fullWidth
                    variant={isFav ? 'secondary' : 'outline'}
                    onClick={handleFavorite}
                    leftIcon={
                      <svg className="w-5 h-5" fill={isFav ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    }
                  >
                    {isFav ? 'Salvo nos Favoritos' : 'Adicionar aos Favoritos'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Button as="link" href={`/meus-anuncios`} fullWidth variant="outline">
                    Gerenciar Anúncio
                  </Button>
                </div>
              )}

              {/* Stats */}
              <div className="flex items-center justify-center gap-6 mt-6 pt-6 border-t border-gray-100 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {anuncioAtual.visualizacoes} visualizações
                </span>
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  {anuncioAtual.favoritosCount} favoritos
                </span>
              </div>

              <p className="text-center text-xs text-gray-400 mt-4">
                Publicado em {formatDate(anuncioAtual.dataCriacao)}
              </p>
            </div>

            {/* Seller Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-4">Vendedor</h3>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold text-xl">
                  {anuncioAtual.vendedor.nome?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold text-gray-900 flex items-center gap-1">
                    {anuncioAtual.vendedor.nome}
                    {anuncioAtual.vendedor.verificado && (
                      <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </p>
                  <p className="text-sm text-gray-500">
                    Membro desde {new Date(anuncioAtual.vendedor.dataCriacao).getFullYear()}
                  </p>
                  {anuncioAtual.vendedor.avaliacaoMedia && (
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-amber-500">★</span>
                      <span className="text-sm font-medium">{anuncioAtual.vendedor.avaliacaoMedia.toFixed(1)}</span>
                      <span className="text-xs text-gray-400">({anuncioAtual.vendedor.totalAvaliacoes} avaliações)</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Fixed Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 lg:hidden">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <p className="text-2xl font-bold text-green-700">{formatPrice(anuncioAtual.preco)}</p>
            {anuncioAtual.precoNegociavel && (
              <p className="text-xs text-gray-500">Negociável</p>
            )}
          </div>
          <Button onClick={() => setShowContactModal(true)} size="lg">
            Contatar
          </Button>
          <button
            onClick={handleFavorite}
            className={`p-3 rounded-lg border-2 transition-colors ${
              isFav
                ? 'bg-red-50 border-red-500 text-red-500'
                : 'border-gray-300 text-gray-600 hover:border-red-500 hover:text-red-500'
            }`}
          >
            <svg className="w-6 h-6" fill={isFav ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Contact Modal */}
      <Modal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        title="Enviar Mensagem"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <img
              src={images[0]?.url || '/placeholder-animal.jpg'}
              alt=""
              className="w-16 h-16 object-cover rounded-lg"
            />
            <div>
              <p className="font-medium text-gray-900 line-clamp-1">{anuncioAtual.titulo}</p>
              <p className="text-green-700 font-bold">{formatPrice(anuncioAtual.preco)}</p>
            </div>
          </div>

          <Textarea
            label="Sua mensagem"
            placeholder="Olá, tenho interesse neste anúncio..."
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value)}
            rows={4}
          />

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowContactModal(false)}
              fullWidth
            >
              Cancelar
            </Button>
            <Button
              onClick={handleContact}
              isLoading={isSending}
              disabled={!mensagem.trim()}
              fullWidth
            >
              Enviar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Image Gallery Modal */}
      {showImageModal && (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
          <button
            onClick={() => setShowImageModal(false)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/20 rounded-full transition-colors"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <button
            onClick={() => setSelectedImage((prev) => Math.max(0, prev - 1))}
            disabled={selectedImage === 0}
            className="absolute left-4 p-2 text-white hover:bg-white/20 rounded-full transition-colors disabled:opacity-30"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <img
            src={mainImage}
            alt={anuncioAtual.titulo}
            className="max-h-[90vh] max-w-[90vw] object-contain"
          />

          <button
            onClick={() => setSelectedImage((prev) => Math.min(images.length - 1, prev + 1))}
            disabled={selectedImage === images.length - 1}
            className="absolute right-4 p-2 text-white hover:bg-white/20 rounded-full transition-colors disabled:opacity-30"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white bg-black/50 px-4 py-2 rounded-full">
            {selectedImage + 1} / {images.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnuncioDetalhe;
